/**
 * Account Dashboard
 *
 * User dashboard showing subscription status, account info, and management options.
 * Integrates with AuthContext for user data and Stripe Customer Portal.
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import MaterialIcon from './MaterialIcon';
import MainHeader from './MainHeader';
import navigationService from '../services/navigationService';
import logger from '../utils/logger';
import './AccountDashboard.css';

const AccountDashboard = () => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [customerPortalLoading, setCustomerPortalLoading] = useState(false);

  useEffect(() => {
    // Wait for auth to finish loading
    if (!authLoading) {
      setLoading(false);
    }
  }, [authLoading]);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      logger.log('⚠️ User not authenticated, redirecting to home');
      navigationService.navigateTo('home');
    }
  }, [loading, isAuthenticated]);

  // Get subscription tier display info
  const getSubscriptionInfo = () => {
    if (!user?.subscription) {
      return {
        tier: 'Free',
        color: '#757575',
        icon: 'account_circle',
        benefits: ['Browse all stickers', 'Create and save scenes', 'Basic search and filtering'],
        limitations: [
          'No early access to new designs',
          'No design voting rights',
          'No subscriber-only stickers',
        ],
      };
    }

    const tier = user.subscription.tier;
    const isActive = user.subscription.status === 'active';

    if (tier === 'pro') {
      return {
        tier: 'Pro',
        color: '#3498db',
        icon: 'star',
        benefits: [
          'Early access to new designs',
          'Vote on future designs',
          'Access to subscriber-only stickers',
          'Priority customer support',
          'No ads',
        ],
        limitations: [],
        isActive,
      };
    }

    if (tier === 'premium') {
      return {
        tier: 'Premium',
        color: '#f39c12',
        icon: 'workspace_premium',
        benefits: [
          'All Pro benefits',
          'Exclusive premium-only designs',
          'Monthly sticker pack',
          'Early access to sales',
          'Custom sticker requests (1 per month)',
          'VIP customer support',
        ],
        limitations: [],
        isActive,
      };
    }

    return getSubscriptionInfo(); // Default to free
  };

  const subscriptionInfo = getSubscriptionInfo();

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Handle manage subscription click (open Stripe Customer Portal)
  const handleManageSubscription = async () => {
    setCustomerPortalLoading(true);
    try {
      // Call backend to create Stripe Customer Portal session
      const response = await fetch('/.netlify/functions/create-customer-portal-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customerId: user.stripeCustomerId,
        }),
      });

      const data = await response.json();

      if (data.url) {
        // Redirect to Stripe Customer Portal
        window.location.href = data.url;
      } else {
        console.error('❌ No portal URL returned');
        alert('Unable to open subscription management. Please try again.');
      }
    } catch (error) {
      console.error('❌ Error opening customer portal:', error);
      alert('Unable to open subscription management. Please try again.');
    } finally {
      setCustomerPortalLoading(false);
    }
  };

  // Handle upgrade click
  const handleUpgrade = () => {
    navigationService.navigateTo('subscription');
  };

  // Loading state
  if (loading) {
    return (
      <>
        <MainHeader currentPage="account" />
        <div className="account-dashboard loading">
          <div className="loading-message">Loading your account...</div>
        </div>
      </>
    );
  }

  // Not authenticated (shouldn't reach here due to redirect)
  if (!isAuthenticated) {
    return null;
  }

  return (
    <>
      <MainHeader currentPage="account" />
      <div className="account-dashboard">
        <div className="dashboard-container">
          {/* Page Header */}
          <div className="dashboard-header">
            <h1>My Account</h1>
            <p className="dashboard-subtitle">Manage your subscription and account settings</p>
          </div>

          {/* Account Info Card */}
          <div className="dashboard-card account-info-card">
            <div className="card-header">
              <MaterialIcon icon="person" size={24} />
              <h2>Account Information</h2>
            </div>
            <div className="card-body">
              <div className="info-row">
                <span className="info-label">Email:</span>
                <span className="info-value">{user.email}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Member Since:</span>
                <span className="info-value">{formatDate(user.createdAt)}</span>
              </div>
              <div className="info-row">
                <span className="info-label">User ID:</span>
                <span className="info-value">{user.uid}</span>
              </div>
            </div>
          </div>

          {/* Subscription Status Card */}
          <div className="dashboard-card subscription-card">
            <div className="card-header">
              <MaterialIcon icon={subscriptionInfo.icon} size={24} />
              <h2>Subscription Status</h2>
            </div>
            <div className="card-body">
              {/* Current Tier */}
              <div className="subscription-tier">
                <div className="tier-badge" style={{ backgroundColor: subscriptionInfo.color }}>
                  <MaterialIcon icon={subscriptionInfo.icon} size={32} />
                  <span className="tier-name">{subscriptionInfo.tier}</span>
                </div>
                {user?.subscription && (
                  <div className="subscription-details">
                    <div className="detail-item">
                      <span className="detail-label">Status:</span>
                      <span className={`status-badge ${user.subscription.status}`}>
                        {user.subscription.status === 'active' ? (
                          <>
                            <MaterialIcon icon="check_circle" size={16} />
                            Active
                          </>
                        ) : (
                          <>
                            <MaterialIcon icon="cancel" size={16} />
                            {user.subscription.status}
                          </>
                        )}
                      </span>
                    </div>
                    {user.subscription.currentPeriodEnd && (
                      <div className="detail-item">
                        <span className="detail-label">
                          {user.subscription.cancelAtPeriodEnd ? 'Expires:' : 'Renews:'}
                        </span>
                        <span className="detail-value">
                          {formatDate(user.subscription.currentPeriodEnd)}
                        </span>
                      </div>
                    )}
                    {user.subscription.cancelAtPeriodEnd && (
                      <div className="cancellation-notice">
                        <MaterialIcon icon="info" size={16} />
                        Your subscription will not renew. You'll have access until{' '}
                        {formatDate(user.subscription.currentPeriodEnd)}.
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Benefits */}
              <div className="benefits-section">
                <h3>Your Benefits</h3>
                <ul className="benefits-list">
                  {subscriptionInfo.benefits.map((benefit, index) => (
                    <li key={index}>
                      <MaterialIcon icon="check_circle" size={16} />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Action Buttons */}
              <div className="subscription-actions">
                {user?.subscription ? (
                  <button
                    className="manage-subscription-btn"
                    onClick={handleManageSubscription}
                    disabled={customerPortalLoading}
                  >
                    <MaterialIcon
                      icon={customerPortalLoading ? 'hourglass_empty' : 'settings'}
                      size={20}
                    />
                    {customerPortalLoading ? 'Opening Portal...' : 'Manage Subscription'}
                  </button>
                ) : (
                  <button className="upgrade-btn" onClick={handleUpgrade}>
                    <MaterialIcon icon="upgrade" size={20} />
                    Upgrade to Pro
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Quick Links Card */}
          <div className="dashboard-card quick-links-card">
            <div className="card-header">
              <MaterialIcon icon="link" size={24} />
              <h2>Quick Links</h2>
            </div>
            <div className="card-body">
              <div className="quick-links-grid">
                <button className="quick-link" onClick={() => navigationService.navigateTo('home')}>
                  <MaterialIcon icon="home" size={24} />
                  <span>Browse Stickers</span>
                </button>
                <button
                  className="quick-link"
                  onClick={() => navigationService.navigateTo('scene-builder')}
                >
                  <MaterialIcon icon="palette" size={24} />
                  <span>Scene Builder</span>
                </button>
                <button className="quick-link" onClick={handleUpgrade}>
                  <MaterialIcon icon="card_membership" size={24} />
                  <span>Subscription Plans</span>
                </button>
                <button className="quick-link" onClick={() => navigationService.navigateTo('faq')}>
                  <MaterialIcon icon="help" size={24} />
                  <span>FAQ</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AccountDashboard;
